import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTable, usePagination, useRowSelect, useSortBy, useGlobalFilter } from 'react-table'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { Checkbox } from '../../components'
import { matchSorter } from 'match-sorter'
import { useViewport } from '../../utils'
import GlobalFilter from './GlobalFilter'

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = val => !val

/**
 * Table Component
 */
const Table = ({
  data,
  columns,
  withCheckbox,
  withPagination,
  noHeader,
  handleCheckbox,
}) => {
  const { isDesktop } = useViewport()

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true
        })
      },
    }),
    []
  )

  const tableConfig = { columns, data, filterTypes }
  const tableHooks = hooks => {
    // If withCheckbox prop is true, append the checkbox to the table
    if (withCheckbox) {
      hooks.visibleColumns.push(columns => [
        {
          id: 'selection',
          // Check All Checkbox
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <Checkbox {...getToggleAllRowsSelectedProps()} />
          ),
          // Row check
          Cell: ({ row }) => (
            <Checkbox {...row.getToggleRowSelectedProps()} />
          )
        },
        ...columns,
      ])
    }
  }

  const table = useTable(
    tableConfig,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    tableHooks
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,

    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    selectedFlatRows,
    state,

    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter
  } = table

  const { pageIndex } = state

  // Pagination
  const endSize = 1
  const midSize = 1
  const currentPage = pageIndex + 1
  const paginationNumbers = useMemo(
    () => {
      const numbers = []
      let dots = false

      for (let index = 1; index <= pageCount; index++) {
        if (index === currentPage) {
          numbers.push(index)
          dots = true
        } else {
          if (index <= endSize || (currentPage && index >= currentPage - midSize && index <= currentPage + midSize) || index > pageCount - endSize ) {
            numbers.push(index)
            dots = true
          } else if (dots) {
            numbers.push('elipsis')
            dots = false
          }
        }
      }

      return numbers
    },
    [currentPage, pageCount]
  )

  React.useEffect(() => {
    if(withCheckbox){
      handleCheckbox(selectedFlatRows)
    }
  }, [withCheckbox, selectedFlatRows, handleCheckbox])

  return (
    <div>
      <table className="table table--striped" {...getTableProps()}>
        {!noHeader && (
          <thead>
            <tr>
              <th
                className="text-left"
                colSpan={visibleColumns.length}
                style={{padding: isDesktop ? 0 : '0 20px'}}
              >
                <GlobalFilter
                  preGlobalFilteredRows={preGlobalFilteredRows}
                  globalFilter={state.globalFilter}
                  setGlobalFilter={setGlobalFilter}
                />
              </th>
            </tr>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} className="text-xs">
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())} className="align-top">
                    {column.render('Header')}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? ' 🔽'
                          : ' 🔼'
                        : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}

        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()} className="text-sm text-gray-444 shadow-on-hover">
                {row.cells.map(cell => {
                  return (
                    <td {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>

      { /* Pagination */ }
      {withPagination && (
        <>
          {
            (paginationNumbers && paginationNumbers.length > 0) &&
            <div className="flex justify-center text-gray-999 mt-8 lg:mt-16 mb-18">
              <button type="button" className="mx-3" onClick={() => previousPage()} disabled={!canPreviousPage}>
                <ChevronLeft/>
              </button>

              {paginationNumbers.map((number, index) => {
                if (number === 'elipsis') {
                  return (
                    <div className="mx-3" key={index}>
                      &hellip;
                    </div>
                  )
                } else {
                  return (
                    <button
                      type="button"
                      className={`mx-3 ${number === currentPage ? 'text-primary' : ''}`}
                      onClick={() => gotoPage(number - 1)}
                      key={index}
                    >
                      {number}
                    </button>
                  )
                }
              })}

              <button type="button" className="mx-3" onClick={() => nextPage()} disabled={!canNextPage}>
                <ChevronRight/>
              </button>
            </div>
          }
        </>
      )}
    </div>
  )
}

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  withCheckbox: PropTypes.bool,
  noHeader: PropTypes.bool,
  withPagination: PropTypes.bool,
  handleCheckbox: PropTypes.func
}

Table.defaultProps = {
  withCheckbox: false,
  noHeader: false,
  withPagination: true,
  handleCheckbox: () => {}
}

export { Table }
